import { useId, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Button, InlineError, Modal, Skeleton, Textarea } from '../../components/ui'
import { useTenant } from '../../tenant/useTenant'
import { useCancelMyBookingMutation, useGetMyBookingQuery } from '../../services/accountApi'
import type { BookingSummary } from '../../types/storefront'
import { bookingWhen, cancelPolicyNote } from './bookingDisplay'
import { cn } from '../../utils/cn'

/** Stitch's four reason tiles, in its own words. */
const REASONS = [
  { value: 'Change of plans', icon: 'sync_alt' },
  { value: 'Found a better time', icon: 'schedule' },
  { value: 'Personal emergency', icon: 'health_and_safety' },
  { value: 'Other', icon: 'more_horiz' },
]

/**
 * I7 — the cancel flow, entered from a booking the customer picked in the list.
 * Reason → confirm dialog → confirmed state, with the salon's notice period stated
 * before the customer commits rather than after they are refused.
 */
export function CancelBookingPage() {
  const { slug, bootstrap } = useTenant()
  const { id } = useParams<{ id: string }>()
  const bookingId = Number(id)
  const reasonName = useId()

  const [reason, setReason] = useState(REASONS[0].value)
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [cancelled, setCancelled] = useState<BookingSummary | null>(null)

  const booking = useGetMyBookingQuery(
    { slug, id: bookingId },
    { skip: !Number.isFinite(bookingId) || bookingId <= 0 },
  )
  const [cancelBooking, { isLoading: cancelling, error: cancelError }] = useCancelMyBookingMutation()

  const policy = cancelPolicyNote(bootstrap.flags.cancel_cutoff_hours)

  if (!bootstrap.flags.customer_cancel_allowed) {
    // The salon does not offer self-service cancelling at all: never show the form.
    return <Navigate to={`/${slug}/account/bookings/${bookingId}`} replace />
  }

  const confirm = async () => {
    const full = [reason, note.trim()].filter(Boolean).join(' — ')
    const result = await cancelBooking({ slug, id: bookingId, reason: full })
    setConfirming(false)
    if ('data' in result && result.data) setCancelled(result.data)
  }

  if (cancelled) return <Confirmed slug={slug} booking={cancelled} />

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <Link
        to={`/${slug}/account/bookings/${bookingId}`}
        className="mb-12 inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary"
      >
        <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
          arrow_back
        </span>
        Back to appointment
      </Link>

      <div className="mx-auto w-full max-w-2xl rounded-xl border border-surface-variant bg-surface-container-lowest p-8 md:p-12">
        <header className="mb-12 text-center">
          <span className="material-symbols-outlined mb-4 block text-[32px] text-outline-variant" aria-hidden="true">
            event_busy
          </span>
          <h1 className="mb-4 font-display text-headline-lg-mobile text-on-background md:text-headline-lg">
            We&apos;re sorry you can&apos;t make it.
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            Would you like to tell us why? This helps us improve our service.
          </p>
        </header>

        {booking.isLoading && <Skeleton lines={3} />}

        {booking.isError && <InlineError error={booking.error} onRetry={() => void booking.refetch()} />}

        {booking.data && !booking.data.can_cancel && (
          <div className="text-center">
            <p className="font-body-md text-body-md text-on-surface">
              This appointment is too close to its start time to cancel online.
              {policy ? ` ${policy}` : ''} Please call the salon and they can help.
            </p>
            <Button variant="outlined" size="lg" className="mt-8" to={`/${slug}/account/bookings/${bookingId}`}>
              Back to appointment
            </Button>
          </div>
        )}

        {booking.data?.can_cancel && (
          <div className="space-y-8">
            <div className="rounded-lg border border-primary/10 bg-surface p-6">
              <p className="font-label-caps text-label-caps uppercase text-primary">Appointment</p>
              <p className="mt-3 font-body-md text-body-md text-on-surface">{bookingWhen(booking.data)}</p>
              <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
                {booking.data.services.map((s) => s.name).join(', ')} · {booking.data.branch.name}
              </p>
              {policy && <p className="mt-4 font-body-md text-sm text-on-surface-variant">{policy}</p>}
            </div>

            {cancelError && <InlineError error={cancelError} />}

            <fieldset>
              <legend className="sr-only">Why are you cancelling?</legend>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {REASONS.map((item) => {
                  const selected = reason === item.value
                  return (
                    <label key={item.value} className="relative cursor-pointer">
                      <input
                        type="radio"
                        name={reasonName}
                        value={item.value}
                        checked={selected}
                        onChange={() => setReason(item.value)}
                        className="peer sr-only"
                      />
                      <div
                        className={cn(
                          'flex h-full items-center gap-3 rounded-lg border bg-surface-container-lowest p-4 transition-colors',
                          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',
                          selected
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-outline-variant text-on-surface hover:border-outline',
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={cn('material-symbols-outlined', selected ? 'text-primary' : 'text-secondary')}
                        >
                          {item.icon}
                        </span>
                        <span className="font-body-md text-body-md">{item.value}</span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </fieldset>

            <Textarea
              label="Additional notes (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
              placeholder="Please share any other details..."
            />

            <div className="flex flex-col items-center gap-6 border-t border-surface-variant pt-8">
              <Button size="lg" fullWidth className="md:w-auto" onClick={() => setConfirming(true)}>
                Confirm cancellation
              </Button>
              <Link
                to={`/${slug}/account`}
                className="font-body-md text-body-md text-primary underline underline-offset-4 transition-colors hover:text-primary-container"
              >
                Keep appointment
              </Link>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        icon="event_busy"
        title="Cancel this appointment?"
        description={
          booking.data
            ? `Your appointment on ${bookingWhen(booking.data)} will be cancelled. This cannot be undone.`
            : undefined
        }
        footer={
          <>
            <Button variant="text" size="lg" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
            <Button size="lg" loading={cancelling} onClick={() => void confirm()} className="bg-error text-on-error">
              Yes, cancel
            </Button>
          </>
        }
      />
    </div>
  )
}

/* ------------------------------------------------------------- confirmed */

function Confirmed({ slug, booking }: { slug: string; booking: BookingSummary }) {
  const staff = booking.services.map((s) => s.staff_name).filter(Boolean)

  const rows = [
    { icon: 'spa', label: 'Service', value: booking.services.map((s) => s.name).join(', ') },
    { icon: 'calendar_today', label: 'Date and time', value: bookingWhen(booking) },
    { icon: 'location_on', label: 'Location', value: booking.branch.name },
    ...(staff.length > 0 ? [{ icon: 'person', label: 'Specialist', value: staff.join(', ') }] : []),
  ]

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <div className="mx-auto w-full max-w-2xl space-y-12 rounded-xl border border-primary/10 bg-surface-container-lowest p-8 text-center md:p-12">
        <div className="flex justify-center">
          <span
            aria-hidden="true"
            className="flex h-20 w-20 items-center justify-center rounded-full bg-surface-container-high text-primary"
          >
            <span className="material-symbols-outlined text-[36px]">cancel</span>
          </span>
        </div>

        <div className="space-y-4">
          <h1 className="font-display text-headline-lg-mobile text-primary md:text-headline-lg">
            Cancellation confirmed
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            Your appointment has been cancelled and the salon has been notified. We hope to welcome you back soon.
          </p>
        </div>

        <div className="space-y-4 rounded-lg border border-primary/10 bg-surface p-6 text-left">
          <h2 className="font-label-caps text-label-caps uppercase text-primary">Cancelled appointment details</h2>
          {rows.map((row, index) => (
            <div
              key={row.label}
              className={cn('flex items-start gap-4', index < rows.length - 1 && 'border-b border-primary/10 pb-4')}
            >
              <span aria-hidden="true" className="material-symbols-outlined mt-1 text-secondary">
                {row.icon}
              </span>
              <div>
                <p className="mb-1 font-label-caps text-label-caps uppercase text-secondary">{row.label}</p>
                <p className="font-body-md text-body-md text-on-surface">{row.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col justify-center gap-4 md:flex-row">
          <Button to={`/${slug}/book`} size="lg">
            Book a new appointment
          </Button>
          <Button to={`/${slug}/account`} variant="outlined" size="lg">
            My appointments
          </Button>
        </div>
      </div>
    </div>
  )
}
